import { useCallback, useEffect, useState } from "react";
import { Plug, ExternalLink, CheckCircle2, CircleDashed, Loader2, ShieldCheck } from "lucide-react";
import { PageHeader, Card } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const CAT_ORDER = ["Business Credit", "Open Finance / Banking", "Consumer Credit"];

function StatusChip({ connected }) {
  return connected ? (
    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-emerald-600"><CheckCircle2 size={12} /> Connected</span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-slate-500"><CircleDashed size={12} /> Not connected</span>
  );
}

function Factory({ f }) {
  if (!f) return null;
  const s = f.summary;
  const tiles = [
    ["Data providers live", `${s.data_providers_live}/${s.data_providers_total}`],
    ["Platform services live", `${s.platform_live}/${s.platform_total}`],
    ["Production requests pending", s.production_requests_pending],
    ["Entitlement overrides", s.feature_overrides],
  ];
  return (
    <div className="space-y-4" data-testid="integrations-factory">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map(([l, v], i) => (
          <div key={l} className="kpi-lux animate-fade-up p-4" style={{ animationDelay: `${i * 70}ms` }} data-testid={`factory-kpi-${i}`}>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
            <div className="mt-1 font-mono text-2xl font-extrabold text-white">{v}</div>
          </div>
        ))}
      </div>
      <Card className="p-5" data-testid="factory-pipeline">
        <div className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#a37d2a]">Provider pipeline</div>
        <div className="divide-y divide-slate-100">
          {f.providers.map((p) => (
            <div key={p.key} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[12px]" data-testid={`factory-row-${p.key}`}>
              <span className="w-56 font-semibold text-slate-800">{p.name}</span>
              <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] uppercase text-slate-500">{p.stage.replace(/_/g, " ")}</span>
              <span className="font-mono text-[10px] text-slate-500">checklist {p.checklist_done}/{p.checklist_total}</span>
              <span className="font-mono text-[10px] text-slate-500">{p.member_connections} member link(s)</span>
              <StatusChip connected={p.status === "CONNECTED"} />
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-5" data-testid="factory-platform">
        <div className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#a37d2a]">Platform services</div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {f.platform.map((p) => (
            <div key={p.name} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-[12px]"><span className="text-slate-700">{p.name} <span className="text-slate-400">· {p.kind}</span></span><StatusChip connected={p.connected} /></div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function Integrations() {
  const [data, setData] = useState(null);
  const [factory, setFactory] = useState(null);
  const [dnb, setDnb] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    api.get("/providers/status").then(({ data }) => setData(data.providers)).catch(() => setData([]));
    api.get("/owner/integrations-factory").then(({ data }) => setFactory(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const testDnb = async () => {
    setTesting(true);
    try {
      const { data } = await api.get("/providers/dnb/health");
      setDnb(data);
      toast[data.token_ok ? "success" : "error"](data.token_ok ? "D&B credentials valid" : (data.configured ? "D&B token failed" : "D&B not configured"));
    } catch { toast.error("Could not run D&B health check"); } finally { setTesting(false); }
  };

  if (!data) return <Loading />;
  const byCat = CAT_ORDER.map((c) => [c, data.filter((p) => p.category === c)]).filter(([, l]) => l.length);

  return (
    <div className="animate-fade-up space-y-6" data-testid="integrations-page">
      <PageHeader eyebrow="Growth & Providers" title="Integrations Factory"
        subtitle="Pipeline stage, credential state, member connections and platform services — all read from real configuration. A provider only shows Connected when its credentials are actually configured." />

      <Factory f={factory} />

      {byCat.map(([cat, list]) => (
        <div key={cat} className="space-y-3">
          <div className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#a37d2a]">{cat}</div>
          <div className="grid gap-4 md:grid-cols-2">
            {list.map((p) => (
              <Card key={p.key} className="p-5" data-testid={`integration-${p.key}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100"><Plug size={16} className="text-slate-500" /></span>
                    <div className="font-head text-[15px] font-bold text-slate-900">{p.name}</div>
                  </div>
                  <StatusChip connected={p.configured} />
                </div>
                <p className="mt-3 text-[12px] leading-relaxed text-slate-500">{p.credentialing}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.env.map((e) => (
                    <span key={e} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[10px] text-slate-600">{e}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <a href={p.docs} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1a5fd0] hover:underline" data-testid={`integration-docs-${p.key}`}>Get credentials <ExternalLink size={12} /></a>
                  {p.key === "dnb" && (
                    <button onClick={testDnb} disabled={testing} data-testid="dnb-test-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:border-slate-400 disabled:opacity-50">
                      {testing ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Test connection
                    </button>
                  )}
                </div>
                {p.key === "dnb" && dnb && (
                  <div className={`mt-3 rounded-lg px-3 py-2 text-[12px] ${dnb.token_ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="dnb-health-result">
                    {dnb.token_ok ? "OAuth token acquired — D&B is live." : dnb.configured ? `Token failed${dnb.error ? ": " + dnb.error : ""}` : "Add DNB_CLIENT_ID and DNB_CLIENT_SECRET, then re-test."}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      ))}
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Credentials are stored as environment secrets — never shown here.</p>
    </div>
  );
}
